// src/setupDatadog.js
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

// The public ACH enrollment page carries its bearer token in the path (/ACH/Enroll/{token}), and every
// RUM event and browser log records the page URL and referrer. The token is replaced before anything
// is sent, so Datadog never holds a usable link.
const ENROLLMENT_TOKEN_IN_PATH = /(\/ACH\/Enroll\/)[^/?#]+/i;
const scrubEnrollmentToken = (url: string) => url.replace(ENROLLMENT_TOKEN_IN_PATH, '$1:token');

const initializeDatadog = async () => {

  const response = await fetch('/datadog-config.json');
  const replaySamplingConfig = await response.json();
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
  const service = process.env.REACT_APP_DATADOG_SERVICE;
  const env = process.env.REACT_APP_ENV;
  const isProdEnv = env.toLowerCase() === "prod";
  const rumSessionSampleRate = isProdEnv ? (replaySamplingConfig.RUM_SESSION_SAMPLE_RATE ?? 50) : 50;
  const logsSessionSampleRate = isProdEnv ? (replaySamplingConfig.LOG_SESSION_SAMPLE_RATE ?? 100) : 100;
  const sessionReplaySampleRate = isProdEnv ? (replaySamplingConfig.SESSION_REPLAY_SESSION_RATE ?? 20) : 0;
  
  datadogLogs.logger.info(
    `datadog session configs, rumSessionSampleRate: ${rumSessionSampleRate}, 
    logsSessionSampleRate: ${logsSessionSampleRate}, sessionReplaySampleRate: ${sessionReplaySampleRate}`);

  const site = process.env.REACT_APP_DATADOG_SITE as "datadoghq.com" | "us3.datadoghq.com" | "us5.datadoghq.com" | "datadoghq.eu" | "ddog-gov.com" | "ap1.datadoghq.com";  
  const privacyLevel = process.env.REACT_APP_DATADOG_PRIVACYLEVEL as "mask" | "mask-user-input" | "allow";
  datadogLogs.init({
    clientToken,
    env,
    site: site,
    service,
    forwardErrorsToLogs: true,
    sessionSampleRate: logsSessionSampleRate,
    beforeSend: (log) => {
      // Drop auto-forwarded network/Axios errors — these are already logged
      // explicitly with structured context in authFetchBaseQuery and UtilTarget.
      // Allow all other auto-forwarded errors (unhandled JS exceptions, etc.)
      // and all explicit logger calls (origin === 'logger') through unchanged.
      if (log.origin !== 'logger' && log.message && (
        log.message.includes('Request failed with status code') ||
        log.message.includes('AxiosError') ||
        log.message.startsWith('Fetch error') ||
        log.message.startsWith('XHR error')
      )) {
        return false;
      }
      if (log.view) {
        log.view.url = scrubEnrollmentToken(log.view.url);
        if (log.view.referrer) log.view.referrer = scrubEnrollmentToken(log.view.referrer);
      }
      return true;
    },
  });

  datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_APPLICATIONID,
    clientToken,
    site: site,
    service,
    env,
    sessionSampleRate: rumSessionSampleRate,
    sessionReplaySampleRate,
    defaultPrivacyLevel: privacyLevel,
    trackUserInteractions: true,
    beforeSend: (event) => {
      event.view.url = scrubEnrollmentToken(event.view.url);
      if (event.view.referrer) event.view.referrer = scrubEnrollmentToken(event.view.referrer);
      return true;
    },
  });
};
export { initializeDatadog };
